<template>
  <BittsModal
    v-if="showRenameModal"
    title="Edit Connection Name"
    :description="description"
    :width="400"
    :use-mask-to-close="true"
    :disabled="v$.newNickname.$errors.length > 0"
    @action="updateNickname"
    @closed="hideRename"
  >
    <BittsInput
      v-model.trim="newNickname"
      name="new-nickname-input"
      :status="v$.newNickname.$errors.length ? 'danger' : 'default'"
      :danger-text="v$.newNickname.$errors?.at(-1)?.$message || ''"
      :placeholder="nickname || externalUrl"
    />
  </BittsModal>
</template>

<script>
import { BittsInput, BittsModal } from '@crossbeam/bitts';

import { useVuelidate } from '@vuelidate/core';
import { helpers, maxLength } from '@vuelidate/validators';
import { mapActions, mapState } from 'pinia';
import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import useAwsPartnerCentral from '@/composables/useAwsPartnerCentral';
import { captureException } from '@/errors';
import { useFeedsStore, useFlashesStore } from '@/stores';
import { returnTrimmedDomain } from '@/utils';

export default {
  name: 'DataSourceRenameModal',
  components: {
    BittsModal,
    BittsInput,
  },
  props: {
    feedId: {
      type: Number,
      required: true,
    },
    nickname: {
      type: String,
      required: true,
    },
  },
  setup() {
    const newNickname = ref('');

    const rules = {
      newNickname: {
        maxLength: helpers.withMessage(
          'That nickname is too long.',
          maxLength(100),
        ),
        unique: helpers.withMessage(
          'That nickname is already in use.',
          (value, _, vm) =>
            !(vm.otherFeedNames && vm.otherFeedNames.includes(value)),
        ),
      },
    };

    const v$ = useVuelidate(rules, { newNickname }, { $lazy: true });

    // AWS Partner Central feeds live on an offline-partner org, so their
    // GET/PATCH must target that org via offline_partner_org_uuid.
    const {
      getAwsFeedById,
      awsPartnerCentralOrgUuid,
      refreshAwsPartnerCentral,
    } = useAwsPartnerCentral();

    return {
      newNickname,
      v$,
      getAwsFeedById,
      awsPartnerCentralOrgUuid,
      refreshAwsPartnerCentral,
    };
  },
  data() {
    return {
      feed: null,
      isAwsFeed: false,
      showRenameModal: false,
    };
  },
  computed: {
    ...mapState(useFeedsStore, ['feeds']),
    description() {
      return `This is how your feed is identified in 
        Crossbeam when using multiple connections.`;
    },
    otherFeedNames() {
      if (!this.feed) return;
      return this.feeds
        .filter((feed) => feed.nickname && feed.id !== this.feed.id)
        .map((feed) => feed.nickname);
    },
    externalUrl() {
      if (!this.feed) return '';
      return returnTrimmedDomain(this.feed.external_base_url);
    },
  },
  watch: {
    newNickname() {
      this.v$.$touch();
    },
  },
  async created() {
    // Warm the AWS cache to detect an AWS feed, but catch: refreshAwsPartnerCentral
    // throws on failure, and an AWS outage must not break rename for other feeds.
    await this.refreshAwsPartnerCentral().catch(captureException);
    const awsFeed = this.getAwsFeedById(this.feedId);
    this.isAwsFeed = !!awsFeed;
    this.feed = awsFeed ?? (await this.fetchFeed());
  },
  mounted() {
    this.showRenameModal = true;
  },
  methods: {
    ...mapActions(useFlashesStore, ['addSuccessFlash', 'addErrorFlash']),
    ...mapActions(useFeedsStore, ['refreshFeedsStore']),
    async hideRename() {
      await this.$router.push({ name: 'data-sources' });
    },
    async fetchFeed() {
      const response = await crossbeamApi.GET('/v0.1/feeds/{id}', {
        params: { path: { id: this.feedId } },
      });
      return response.data;
    },
    async updateNickname() {
      try {
        if (this.v$.$invalid) return;

        if (this.newNickname !== '') {
          const uuid = this.isAwsFeed
            ? await this.awsPartnerCentralOrgUuid()
            : null;
          // Without the offline-partner uuid the PATCH would target the customer
          // org, so abort rather than rename against the wrong org (matches delete).
          if (this.isAwsFeed && !uuid) {
            throw new Error(
              'Missing AWS Partner Central org uuid; aborting rename',
            );
          }
          await crossbeamApi.PATCH('/v0.1/feeds/{id}', {
            params: {
              path: {
                id: this.feedId,
              },
              ...(uuid && { query: { offline_partner_org_uuid: uuid } }),
            },
            body: {
              is_paused: this.feed.is_paused,
              frequency: this.feed.frequency,
              nickname: this.newNickname,
            },
          });
          this.addSuccessFlash({ message: 'Data Source Renamed' });
        }
        await this.refreshFeedsStore();
        // AWS Partner Central feeds live outside FeedsStore, so refresh them too
        // or the list keeps showing the old nickname. Catch so a failed refetch
        // doesn't report an error on an otherwise-successful rename.
        if (this.isAwsFeed) {
          await this.refreshAwsPartnerCentral().catch(captureException);
        }
        this.showRenameModal = false;
        this.$router.push({ name: 'data-sources' });
      } catch (_err) {
        this.addErrorFlash({ message: 'Connection could not be renamed.' });
      }
    },
  },
};
</script>
